import { yupResolver } from '@hookform/resolvers/yup';
import posthog from 'posthog-js';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import {
  Avatar,
  AvatarFallback,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormLabel,
  StatusBadges,
  StatusStyle,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  Typography,
  useSnackbar
} from '@growdash/design-system/atoms';

import {
  ConfirmDeleteDialog,
  DataTable,
  FormProvider,
  RHFAutocomplete,
  RHFInput
} from '@growdash/design-system/molecules';
// utils
import { InvitationStatus } from '@utils/interfaces';
// context
import { useAuthContext } from '@context/auth/use-auth-context';
// routes
// hooks
import { useJune } from '@hooks/use-june';
// api
import { Loader2, Plus, Trash2 } from 'lucide-react';

import { UsersControllerGetUsersByClientIdStatusEnum } from '@growdash/identity/generated';
import { identityClient } from '@lib/identity';
import { RoleType } from '@utils/roles';
import { getSessionUser, setSession } from '@utils/session';

const { usersAPI, authAPI } = identityClient;

const STATUS_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: InvitationStatus.Verified, label: 'Active' },
  { value: InvitationStatus.Pending, label: 'Pending Invitation' },
  { value: InvitationStatus.Expired, label: 'Expired' }
];

interface StatusCounts {
  all: number;
  verified: number;
  pending: number;
  expired: number;
}

export default function UsersTab() {
  const { enqueueSnackbar } = useSnackbar();
  const { user, logout } = useAuthContext();
  const juneHook = useJune('q3ClmW5XJpuu6QHF') as any;
  const [tableData, setTableData] = useState<any | null>(null);
  const [currentUserTab, setCurrentUserTab] = useState('All');
  const [openDeleteConform, setOpenDeleteConform] = useState<number>(0);
  const [tableLoading, setLoading] = useState<boolean>(false);
  const [allClients, setAllClients] = useState<any[]>([]);
  const [filteredClients, setFilteredClients] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [emailNotifyUpdateData, setEmailNotifyData] =
    useState<EmailNotificationPayload | null>(null);
  const [statusCounts, setStatusCounts] = useState<StatusCounts>({
    all: 0,
    verified: 0,
    pending: 0,
    expired: 0
  });
  type EmailNotificationPayload = {
    id: any;
    emailNotificationsEnabled: boolean;
  };

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is required')
      .email('Email must be a valid email address'),

    fullName: Yup.string().required('Name is required')
  });

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues: { email: '', fullName: '' }
  });

  const { reset, handleSubmit } = methods;

  // Get User API call
  const fetchClient = useCallback(
    async (page: number, take: number, activeTab: string) => {
      try {
        setLoading(true);
        const userResponse = await usersAPI.usersControllerGetUsersByClientId(
          page,
          take,
          activeTab === 'All'
            ? undefined
            : (activeTab as UsersControllerGetUsersByClientIdStatusEnum)
        );
        const { data } = userResponse;
        setTableData(data);
        setStatusCounts({
          // TODO: fix types
          // @ts-ignore
          all: data?.metadata?.total,
          // @ts-ignore
          verified: data?.metadata?.active,
          // @ts-ignore
          pending: data?.metadata?.pending,
          // @ts-ignore
          expired: data?.metadata?.expired
        });
      } catch (error) {
        enqueueSnackbar(error?.message ?? 'something went wrong', {
          variant: 'error'
        });
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar]
  );
  const getClients = useCallback(async () => {
    try {
      if (user?.roles?.includes(RoleType.MANAGER)) {
        const response =
          await usersAPI.usersControllerGetClientsForCustomerSuccessUser();
        setAllClients(response.data);
      } else {
        const response = await usersAPI.usersControllerGetClientsForClients();
        setAllClients(response.data);
      }
    } catch (error) {
      enqueueSnackbar(error?.message, { variant: 'error' });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add User API call
  const onSubmit = handleSubmit(async data => {
    try {
      await usersAPI.usersControllerCreate(data);
      enqueueSnackbar('User Invited success!');
      reset();
    } catch (error) {
      enqueueSnackbar(error?.message, { variant: 'error' });
    } finally {
      fetchClient(1, rowsPerPage, currentUserTab);
    }
  });

  const handleDeleteUser = async (id: number) => {
    try {
      await usersAPI.usersControllerDeleteUser(id);
      enqueueSnackbar('User Delete success!');
    } catch (error) {
      enqueueSnackbar(error?.message, { variant: 'error' });
    } finally {
      fetchClient(1, rowsPerPage, currentUserTab);
      setOpenDeleteConform(0);
    }
  };

  const handleEmailNotificationOfUser = async (
    userVal: EmailNotificationPayload
  ) => {
    try {
      if (!userVal?.emailNotificationsEnabled) {
        await usersAPI.usersControllerEmailSubscribe(userVal.id);
        enqueueSnackbar('Successfully subscribed email notification!');
      } else {
        await usersAPI.usersControllerEmailUnsubscribe(userVal.id);
        enqueueSnackbar('Successfully unsubscribed email notification!');
      }
    } catch (error) {
      enqueueSnackbar(error?.message, { variant: 'error' });
    } finally {
      fetchClient(1, rowsPerPage, currentUserTab);
      setEmailNotifyData(null);
    }
  };

  const getStatusString: (status: InvitationStatus) => string = (
    status: string
  ) => {
    switch (status) {
      case InvitationStatus.Verified:
        return 'Active';
      case InvitationStatus.Pending:
        return 'Pending Invitation';
      default:
        return 'Expired';
    }
  };

  const onChangeStatus = useCallback(
    (newValue: string) => {
      setCurrentUserTab(newValue);
    },
    [setCurrentUserTab]
  );

  const handleLogout = async () => {
    posthog.reset();
    // The logout function will handle clearing session, analytics, and navigation
    logout();
  };

  const tableLabels = [
    {
      id: 'fullName',
      header: 'Name',
      accessorKey: 'fullName',
      cell: ({ row }: any) => {
        const fullName = row?.getValue('fullName');
        return <div>{fullName}</div>;
      }
    },
    {
      id: 'email',
      header: 'Email',
      accessorKey: 'email',
      cell: ({ row }: any) => {
        const email = row?.getValue('email');
        return <div>{email}</div>;
      }
    },
    {
      id: 'emailNotificationsEnabled',
      header: 'Email Notification',
      accessorKey: 'emailNotificationsEnabled',
      cell: ({ row }: any) => {
        const emailNotificationsEnabled = row?.getValue(
          'emailNotificationsEnabled'
        );
        return (
          <div
            className='flex items-center gap-2 cursor-pointer'
            onClick={() => {
              setEmailNotifyData({
                id: row.original.id,
                emailNotificationsEnabled: emailNotificationsEnabled
              });
            }}
          >
            {emailNotificationsEnabled ? 'Subscribed' : 'Unsubscribed'}

            <Switch checked={emailNotificationsEnabled} />
          </div>
        );
      }
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: ({ row }: any) => {
        const status: InvitationStatus = row?.getValue('status');
        return (
          <div>
            <StatusBadges
              text={getStatusString(status)}
              status={
                status === InvitationStatus.Verified
                  ? StatusStyle.Green
                  : status === InvitationStatus?.Pending
                    ? StatusStyle.Blue
                    : StatusStyle.Red
              }
            />
          </div>
        );
      }
    },
    {
      id: 'action',
      header: 'Action',
      accessorKey: 'action',
      align: 'center',
      cell: ({ row }: any) => {
        return (
          <div
            className='text-center cursor-pointer bg-[#FAE1E1] bg-opacity-25 rounded-md 
            p-1.5 max-w-min'
            onClick={() => setOpenDeleteConform(row?.original?.id)}
          >
            <Trash2 size={20} className='text-[#C92222] ' />
          </div>
        );
      }
    }
  ];

  const handelChangeToken = useCallback(
    async (id: number) => {
      try {
        const response = await authAPI.authControllerRegenerateToken({
          newClientId: id
        });
        const { access_token: accessToken, refresh_token: refreshToken } =
          response.data;
        if (accessToken) {
          await setSession(accessToken as any, refreshToken as any);
          const tokenUser = getSessionUser();
          if (juneHook) {
            juneHook.identify(tokenUser.username.toLowerCase(), {
              email: tokenUser.username,
              CSUser: tokenUser.name,
              country: tokenUser.country.name,
              roles: tokenUser.roles
            });
            juneHook.track(
              'CS visited Client',
              {
                visitedClient: tokenUser.client.name,
                CS_user: tokenUser.name
              },
              {
                // Add the GROUP_ID here to track this event on behalf of a workspace
                context: {
                  groupId: `Customer Service`
                }
              }
            );
          }
          window.location.reload();
        }
      } catch (error) {
        enqueueSnackbar(error?.message, { variant: 'error' });
      }
    },
    [enqueueSnackbar, juneHook]
  );

  useEffect(() => {
    fetchClient(page + 1, rowsPerPage, currentUserTab);
    getClients();
  }, [page, rowsPerPage, currentUserTab, fetchClient, getClients]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchClient(newPage + 1, rowsPerPage, currentUserTab);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchClient(page + 1, newRowsPerPage, currentUserTab);
  };

  return (
    <>
      {/* <Alert sx={{
        background: '#FAEDED',
        marginTop: 3,
        alignItems: 'center',
        fontWeight: 600,
          color: '#212B36',
          "& .MuiAlert-icon": {
            color: '#C92222',
          }
        }}
        severity="warning">
        Payment Failed: We were unable to process your payment. Please verify your payment details and try again or use a different payment method. If the issue is not resolved within 7 days, your access to the dashboard will be suspended.
      </Alert> */}
      <div className='mt-4'>
        <div className='flex items-center justify-between gap-4 h-64 mb-4'>
          {(user?.groupId || user?.roles?.includes(RoleType.MANAGER)) && (
            <Card className='w-1/2 h-full'>
              <CardHeader>
                <CardTitle>
                  <div className='flex items-start justify-between gap-1 '>
                    <div className='flex flex-col gap-0'>
                      <Typography variant='h4' className='leading-none'>
                        {user?.client_name}
                      </Typography>
                      <Typography fontWeight={500} variant='subtitle2'>
                        {user?.username}
                      </Typography>
                    </div>
                    <Avatar className='w-10 h-10'>
                      <AvatarFallback>
                        {user?.client_name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FormProvider methods={methods} onSubmit={handleLogout}>
                  <div className='w-full'>
                    <FormLabel className='text-md mb-1 ml-0.5'>
                      Switch Client
                    </FormLabel>

                    <RHFAutocomplete
                      name='brand'
                      getOptionId={option => option.id}
                      placeholder='Select Client Name'
                      options={
                        filteredClients.length ? filteredClients : allClients
                      }
                      extractLabel={(option: any) => option?.name || ''}
                      onInputChange={(inputValue: string) => {
                        if (!inputValue) {
                          setFilteredClients([]);
                          return;
                        }
                        const filtered = allClients.filter(option =>
                          option.name
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                        );
                        setFilteredClients(filtered);
                      }}
                      renderOption={(option: any) => (
                        <li
                          className='cursor-pointer flex gap-4'
                          key={option?.id}
                          onClick={() => handelChangeToken(option.id)}
                        >
                          <Avatar
                            style={{
                              width: 32,
                              height: 32
                            }}
                          >
                            <AvatarFallback>
                              {option?.name?.charAt(0)?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>

                          <Typography variant='inherit'>
                            {option?.name}
                          </Typography>
                        </li>
                      )}
                    />
                  </div>
                  <div className='flex justify-end mt-6'>
                    <Button
                      type='submit'
                      className='ml-auto shadow-none text-[#C92222] bg-[#FAE1E1] hover:bg-[#fdf3f3]'
                    >
                      Log out
                    </Button>
                  </div>
                </FormProvider>
              </CardContent>
            </Card>
          )}
          <Card className='w-1/2 h-full'>
            <CardHeader>
              <CardTitle>
                <Typography variant='h4'>Add New User</Typography>
                <Typography variant='subtitle2'>
                  {user?.client_name ?? ''} has {allClients?.length ?? 0} users
                </Typography>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FormProvider methods={methods} onSubmit={onSubmit}>
                <div className='w-full flex items-center justify-between my-2 gap-1'>
                  <div className='w-1/2'>
                    <RHFInput name='fullName' label='Name' />
                  </div>
                  <div className='w-1/2'>
                    <RHFInput
                      // haneldErrorMsg={setErrorMsg}
                      name='email'
                      label='Email'
                    />
                  </div>
                </div>
                <Button type='submit' className='bg-[#02006D] w-full mt-4'>
                  <Plus />
                  Add
                </Button>
              </FormProvider>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <Tabs value={currentUserTab} onValueChange={onChangeStatus}>
              <TabsList>
                {STATUS_OPTIONS.map(tab => (
                  <TabsTrigger key={tab.value} value={tab.value}>
                    {tab.label}
                    {` (${
                      statusCounts[
                        tab.value.toLowerCase() as keyof StatusCounts
                      ]
                    })`}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </CardHeader>

          <CardContent>
            {tableLoading ? (
              <div className='py-4'>
                <Loader2 className='mx-auto animate-spin' size={32} />
              </div>
            ) : (
              <DataTable
                columns={tableLabels}
                data={tableData?.data ?? []}
                isPaginationActive
                isServerSide
                pagination={{
                  pageSize: rowsPerPage,
                  pageIndex: page,
                  pageCount: Math.ceil((tableData?.total ?? 0) / rowsPerPage),
                  onPageChange: handlePageChange,
                  onRowsPerPageChange: handleRowsPerPageChange
                }}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmDeleteDialog
        open={!!openDeleteConform}
        onClose={() => setOpenDeleteConform(0)}
        title='Delete User?'
        content='This action can not be undone. 
        This will permanently delete your user'
        action={
          <Button
            color='error'
            onClick={() => handleDeleteUser(openDeleteConform)}
          >
            Delete
          </Button>
        }
      />
      <ConfirmDeleteDialog
        open={!!emailNotifyUpdateData}
        onClose={() => setEmailNotifyData(null)}
        title={
          emailNotifyUpdateData?.emailNotificationsEnabled
            ? 'Unsubscribe Email Notification'
            : 'Subscribe Email Notification'
        }
        content={
          emailNotifyUpdateData?.emailNotificationsEnabled
            ? 'Confirm Email notification unsubscription'
            : 'Confirm Email notification subscription'
        }
        action={
          <Button
            onClick={() =>
              handleEmailNotificationOfUser(
                emailNotifyUpdateData as EmailNotificationPayload
              )
            }
          >
            Confirm
          </Button>
        }
      />
    </>
  );
}
